import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  MapPin,
  Calendar,
  Clock,
  Users,
  Baby,
  Briefcase,
  User,
  Mail,
  Phone,
  MessageSquare,
  CreditCard,
  Shield,
  ArrowRight,
  ArrowLeft,
  Check,
  Plane,
  Car,
  Bus,
  AlertCircle,
  Info,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { toast } from "sonner";
import Header from "../components/layout/Header";
import PlacesAutocomplete from "../components/booking/PlacesAutocomplete";
import PhoneField from "@/components/ui/phone-field";
import { FlightDetailsSection } from "@/components/booking/FlightDetailsSection";
import { toParisISO } from "@/lib/time-utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Locations for One-Way and Round Trip
const TRANSFER_LOCATIONS = {
  airports: {
    label: "Paris Airports",
    locations: [
      {
        value: "Charles de Gaulle Airport (CDG)",
        label: "Charles de Gaulle (CDG)",
      },
      { value: "Paris Orly Airport (ORY)", label: "Paris Orly (ORY)" },
      { value: "Beauvais Airport (BVA)", label: "Beauvais (BVA)" },
    ],
  },
  disney: {
    label: "Disneyland Paris",
    locations: [
      { value: "Disneyland Park", label: "Disneyland Park" },
      {
        value: "Hotel Near Disneyland",
        label: "Hotel Near Disneyland",
        needsAddress: true,
      },
      {
        value: "Apartment/Airbnb near Disneyland",
        label: "Apartment/Airbnb near Disneyland",
        needsAddress: true,
      },
    ],
  },
  paris: {
    label: "Paris",
    locations: [
      { value: "Paris City", label: "Paris City", needsAddress: true },
      {
        value: "Paris Train Station",
        label: "Paris Train Station",
        needsAddress: true,
      },
      { value: "Paris Hotels", label: "Paris Hotels", needsAddress: true },
      {
        value: "Paris Apartments/Airbnb",
        label: "Paris Apartments/Airbnb",
        needsAddress: true,
      },
    ],
  },
  attractions: {
    label: "Attractions",
    locations: [
      { value: "La Defense", label: "La Defense" },
      { value: "Chateau De Versailles", label: "Chateau De Versailles" },
      { value: "Versailles", label: "Versailles" },
    ],
  },
  tours: {
    label: "Tours",
    locations: [
      { value: "Mont Saint-Michel", label: "Mont Saint-Michel" },
      { value: "Normandy", label: "Normandy" },
    ],
  },
  other: {
    label: "Other",
    locations: [
      { value: "Other Location", label: "Other Location", needsAddress: true },
    ],
  },
};

// Locations for Hourly bookings (no Tours)
const HOURLY_LOCATIONS = {
  airports: TRANSFER_LOCATIONS.airports,
  disney: TRANSFER_LOCATIONS.disney,
  paris: TRANSFER_LOCATIONS.paris,
  attractions: {
    label: "Attractions",
    locations: [
      { value: "La Defense", label: "La Defense" },
      { value: "Chateau De Versailles", label: "Chateau De Versailles" },
      { value: "Versailles", label: "Versailles" },
    ],
  },
  other: {
    label: "Other",
    locations: [
      { value: "Other Location", label: "Other Location", needsAddress: true },
    ],
  },
};

// Locations that require address input
const LOCATIONS_NEEDING_ADDRESS = [
  "Hotel Near Disneyland",
  "Apartment/Airbnb near Disneyland",
  "Paris City",
  "Paris Train Station",
  "Paris Hotels",
  "Paris Apartments/Airbnb",
  "Other Location",
];

// Generate time slots
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const h = hour.toString().padStart(2, "0");
      const m = min.toString().padStart(2, "0");
      slots.push(`${h}:${m}`);
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

// Luggage info
const LUGGAGE_INFO = {
  car: { checked: 2, hand: 2 },
  van: { checked: 8, hand: 8 },
};

const Booking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if this is a quote request from widget
  const isQuoteFromWidget = searchParams.get("quoteRequired") === "true";

  // Get price passed from widget for consistency
  const initialPrice = searchParams.get("price")
    ? parseInt(searchParams.get("price"))
    : null;
  const initialNightSurcharge = searchParams.get("nightSurcharge")
    ? parseInt(searchParams.get("nightSurcharge"))
    : 0;

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(initialPrice);
  const [nightSurcharge, setNightSurcharge] = useState(initialNightSurcharge);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isReturnDateOpen, setIsReturnDateOpen] = useState(false);
  const [hasFormChanged, setHasFormChanged] = useState(false);

  // Initialize form from URL params (from widget)
  const [formData, setFormData] = useState({
    tripType: searchParams.get("tripType") || "oneway",
    pickupLocation: searchParams.get("pickup") || "",
    dropoffLocation: searchParams.get("dropoff") || "",
    pickupAddress: searchParams.get("pickupAddress") || "",
    dropoffAddress: searchParams.get("dropoffAddress") || "",
    returnPickupLocation: searchParams.get("returnPickup") || "",
    returnPickupAddress: searchParams.get("returnDropoff") || "",
    returnDropoffLocation: searchParams.get("returnPickupAddress") || "",
    returnDropoffAddress: searchParams.get("returnDropoffAddress") || "",
    date: searchParams.get("date") ? new Date(searchParams.get("date")) : null,
    time: searchParams.get("time") || "",
    returnDate: searchParams.get("returnDate")
      ? new Date(searchParams.get("returnDate"))
      : null,
    returnTime: searchParams.get("returnTime") || "",
    adults: searchParams.get("adults") || "1",
    kids: searchParams.get("kids") || "0",
    nightSurcharge: nightSurcharge || initialNightSurcharge,
    hours: searchParams.get("hours") || "2",
    vehicleChoice: "Car",
    luggage: "2",
    hand_luggages: searchParams.get("hand_luggages") || "0",
    checked_luggages: searchParams.get("checked_luggages") || "0",
    flightDetailType: "ARRIVAL",
    arrivalFlightNo: "",
    arrivalFrom: "",
    departureFlightNo: "",
    departureTo: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    whatsappOptin: false,
    notes: "",
    paymentMethod: "cash",
    gdprConsent: false,
    termsConsent: false,
    flightDetailType: "ARRIVAL",
  });

  const totalPassengers = parseInt(formData.adults) + parseInt(formData.kids);

  // Check if "Other Location" is selected - requires quote, no automatic pricing
  const isOtherLocationPickup = formData.pickupLocation === "Other Location";
  const isOtherLocationDropoff = formData.dropoffLocation === "Other Location";
  const isQuoteRequired = isOtherLocationPickup || isOtherLocationDropoff;

  // Check if night pricing applies
  const isNightPricing = nightSurcharge > 0;

  // Check if selected time falls within night pricing hours (21:00 - 05:00)
  const isNightTimeSelected = () => {
    if (!formData.time) return false;
    const [hours] = formData.time.split(":");
    const hour = parseInt(hours);
    return hour >= 21 || hour < 5;
  };

  // Show night pricing notice if either: price calculated with surcharge OR time is in night hours
  const showNightPricingNotice = isNightPricing || isNightTimeSelected();

  // Get appropriate locations based on trip type
  const getLocations = () => {
    return formData.tripType === "hourly"
      ? HOURLY_LOCATIONS
      : TRANSFER_LOCATIONS;
  };

  // Check if location needs address
  const needsAddressInput = (location) => {
    return LOCATIONS_NEEDING_ADDRESS.includes(location);
  };

  // // Vehicle selection logic
  // const getVehicleSelectionMode = () => {
  //   if (totalPassengers <= 3) return "selectable"; // Can choose Car or Van
  //   if (totalPassengers <= 8) return "van-only"; // Van mandatory
  //   return "multi-vehicle"; // 9+ passengers
  // };

  const getVehicleSelectionMode = () => {
    const passengers = Number(formData.adults) + Number(formData.kids);
    const hand = Number(formData.hand_luggages || 0);
    const checked = Number(formData.checked_luggages || 0);

    const vehiclesNeeded = calculateVehiclesNeeded(passengers, hand, checked);

    if (!vehiclesNeeded) return "selectable";

    const { cars = 0, vans = 0 } = vehiclesNeeded;

    // 1 vehicle only
    if (cars === 1 && vans === 0) return "selectable"; // allow user to pick Car or upgrade to Van
    if (cars === 0 && vans === 1) return "van-only"; // van is required

    // multiple vehicles
    return "multi-vehicle";
  };

  const CAR_CAPACITY = 3;
  const VAN_CAPACITY = 8;

  const calculateVehiclesNeeded = (
    totalPassengers,
    handLuggages = 0,
    checkedLuggages = 0,
  ) => {
    let cars = 0;
    let vans = 0;

    const luggageCapacity = (c, v) => ({
      hand: c * LUGGAGE_INFO.car.hand + v * LUGGAGE_INFO.van.hand,
      checked: c * LUGGAGE_INFO.car.checked + v * LUGGAGE_INFO.van.checked,
    });

    // tolerance (same as backend)
    const CAR_HAND_TOLERANCE = 1;
    const CAR_CHECKED_TOLERANCE = 0;

    // ---- STEP 1: passenger-first fleet ----
    if (totalPassengers <= CAR_CAPACITY) {
      cars = 1;
    } else if (totalPassengers <= VAN_CAPACITY) {
      vans = 1;
    } else {
      vans = Math.floor(totalPassengers / VAN_CAPACITY);
      const remaining = totalPassengers % VAN_CAPACITY;
      if (remaining > 0) {
        if (remaining <= CAR_CAPACITY) cars = 1;
        else vans += 1;
      }
    }

    // ---- STEP 2/3: rebalance for luggage ----
    while (true) {
      const cap = luggageCapacity(cars, vans);

      // special single-car tolerance: (hand=3, checked=1) stays car
      // if (cars === 1 && vans === 0) {
      //   const effectiveCarHand = LUGGAGE_INFO.car.hand + CAR_HAND_TOLERANCE;
      //   const effectiveCarChecked = LUGGAGE_INFO.car.checked + CAR_CHECKED_TOLERANCE;
      //   if (handLuggages <= effectiveCarHand && checkedLuggages <= effectiveCarChecked) {
      //     break;
      //   }
      // }

      if (cars === 1 && vans === 0) {
        if (
          handLuggages <= LUGGAGE_INFO["car"]["hand"] + 1 &&
          checkedLuggages <= 1
        ) {
          break;
        }
      }

      // normal fit check
      if (handLuggages <= cap.hand && checkedLuggages <= cap.checked) {
        break;
      }

      // upgrade ONE car -> ONE van if we are car-only and luggage doesn't fit
      if (vans === 0 && cars >= 1) {
        cars -= 1;
        vans += 1;
        continue;
      }

      // remaining shortage
      const handShort = Math.max(0, handLuggages - cap.hand);
      const checkedShort = Math.max(0, checkedLuggages - cap.checked);

      const carHand = LUGGAGE_INFO.car.hand;
      const carChecked = LUGGAGE_INFO.car.checked;
      const vanHand = LUGGAGE_INFO.van.hand;
      const vanChecked = LUGGAGE_INFO.van.checked;

      // if ONE car can cover remaining shortage, prefer adding car
      if (handShort <= carHand && checkedShort <= carChecked) {
        cars += 1;
        continue;
      }

      // otherwise add the vehicle that reduces shortage more (ties -> car)
      const carHelp =
        Math.min(handShort, carHand) + Math.min(checkedShort, carChecked);
      const vanHelp =
        Math.min(handShort, vanHand) + Math.min(checkedShort, vanChecked);

      if (vanHelp > carHelp) vans += 1;
      else cars += 1;
    }

    const cap = luggageCapacity(cars, vans);

    return {
      cars,
      vans,
      totalPassengersCapacity: cars * CAR_CAPACITY + vans * VAN_CAPACITY,
      luggageCapacity: cap,
      luggageRequested: { hand: handLuggages, checked: checkedLuggages },
    };
  };

  // Update form field and mark form as changed
  const updateForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Mark form as changed when pricing-related fields are modified
    if (
      [
        "tripType",
        "pickupLocation",
        "dropoffLocation",
        "date",
        "time",
        "adults",
        "kids",
        "hours",
        "vehicleChoice",
        "hand_luggages",
        "checked_luggages",
      ].includes(field)
    ) {
      setHasFormChanged(true);
    }
  };

  // Calculate price
  const calculatePrice = useCallback(async () => {
    // If "Other Location" is selected, don't calculate - quote required
    if (isQuoteRequired) {
      setCalculatedPrice(null);
      setNightSurcharge(0);
      setVehicleInfo(null);
      return;
    }

    if (!formData.pickupLocation || !formData.date || !formData.time) return;
    if (formData.tripType !== "hourly" && !formData.dropoffLocation) return;

    try {
      // 1. Add standard mandatory parameters
      const params = new URLSearchParams({
        trip_type: formData.tripType,
        pickup_location: formData.pickupLocation,
        dropoff_location: formData.dropoffLocation || formData.pickupLocation,
        pickup_datetime: toParisISO(formData.date, formData.time),
        adults: formData.adults,
        kids: formData.kids,
        hand_luggages: formData.hand_luggages,
        checked_luggages: formData.checked_luggages,
      });

      // 2. Safely add Return fields ONLY if applicable and completely avoid empty strings
      if (formData.tripType === "return") {
        if (formData.returnPickupLocation) {
          params.set("return_pickup_location", formData.returnPickupLocation);
        }
        if (formData.returnDropoffLocation) {
          params.set("return_dropoff_location", formData.returnDropoffLocation);
        }
        if (formData.returnDate && formData.returnTime) {
          params.set(
            "return_pickup_datetime",
            toParisISO(formData.returnDate, formData.returnTime),
          );
        }
      }

      // 3. Add Hourly fields
      if (formData.tripType === "hourly") {
        params.set("hours", formData.hours);
      }

      // 4. For 1-3 passengers, include vehicle choice
      if (totalPassengers <= 3) {
        params.set("vehicle_choice", formData.vehicleChoice);
      }

      const response = await fetch(
        `${API_URL}/api/pricing/calculate?${params}`,
        {
          method: "POST",
        },
      );

      if (response.ok) {
        const data = await response.json();
        setCalculatedPrice(data.total_price);
        setNightSurcharge(data.night_surcharge || 0);
        setVehicleInfo({
          type: data.vehicle_type,
          vehicles: data.vehicles_needed,
          breakdown: data.breakdown,
        });
      }
    } catch (error) {
      console.error("Error calculating price:", error);
    }
  }, [formData, totalPassengers, isQuoteRequired]);

  // Recalculate price only when form values change AFTER initial load
  // If we have a price from the widget and form hasn't changed, don't recalculate
  useEffect(() => {
    // Skip recalculation if we have initial price from widget and form hasn't changed yet
    if (initialPrice !== null && !hasFormChanged) {
      return;
    }
    calculatePrice();
  }, [
    formData.tripType,
    formData.pickupLocation,
    formData.dropoffLocation,
    formData.date,
    formData.time,
    formData.adults,
    formData.kids,
    formData.hours,
    formData.vehicleChoice,
    formData.hand_luggages,
    formData.checked_luggages,
    calculatePrice,
    initialPrice,
    hasFormChanged,
  ]);

  // Auto-select vehicle based on passengers
  useEffect(() => {
    if (totalPassengers > 3) {
      updateForm("vehicleChoice", "Van");
    }
  }, [totalPassengers]);

  useEffect(() => {
    const passengers = Number(formData.adults) + Number(formData.kids);

    const hand = Number(formData.hand_luggages || 0);
    const checked = Number(formData.checked_luggages || 0);

    const { cars, vans } = calculateVehiclesNeeded(passengers, hand, checked);
    // const {cars,vans } = fetchVehicleNeeds()

    // If any van is needed → auto-select Van
    if (vans > 0) {
      updateForm("vehicleChoice", "Van");
    } else {
      updateForm("vehicleChoice", "Car");
    }
  }, [
    formData.adults,
    formData.kids,
    formData.hand_luggages,
    formData.checked_luggages,
  ]);

  // Get luggage info based on vehicle
  const getLuggageInfo = () => {
    const selectionMode = getVehicleSelectionMode();
    if (selectionMode === "multi-vehicle") {
      const hand = Number(formData.hand_luggages || 0);
      const checked = Number(formData.checked_luggages || 0);
      const vehicles = calculateVehiclesNeeded(totalPassengers, hand, checked);
      return {
        checked:
          vehicles.cars * LUGGAGE_INFO.car.checked +
          vehicles.vans * LUGGAGE_INFO.van.checked,
        hand:
          vehicles.cars * LUGGAGE_INFO.car.hand +
          vehicles.vans * LUGGAGE_INFO.van.hand,
      };
    }
    return formData.vehicleChoice === "Van"
      ? LUGGAGE_INFO.van
      : LUGGAGE_INFO.car;
  };

  // Submit booking
  const handleSubmit = async () => {
    if (!formData.termsConsent) {
      toast.error("Please accept the Terms and Conditions");
      return;
    }
    if (!formData.gdprConsent) {
      toast.error("Please accept the privacy policy");
      return;
    }

    // Validate address for Other Location
    if (isOtherLocationPickup && !formData.pickupAddress) {
      toast.error("Please enter your pick-up location address");
      return;
    }
    if (isOtherLocationDropoff && !formData.dropoffAddress) {
      toast.error("Please enter your drop-off location address");
      return;
    }

    setIsSubmitting(true);

    try {
      // Build pickup/dropoff with addresses if needed
      let pickup = formData.pickupLocation;
      let dropoff = formData.dropoffLocation;

      if (
        needsAddressInput(formData.pickupLocation) &&
        formData.pickupAddress
      ) {
        pickup = `${formData.pickupLocation}: ${formData.pickupAddress}`;
      }
      if (
        needsAddressInput(formData.dropoffLocation) &&
        formData.dropoffAddress
      ) {
        dropoff = `${formData.dropoffLocation}: ${formData.dropoffAddress}`;
      }

      const bookingData = {
        trip_type: formData.tripType,
        pickup_location: pickup,
        dropoff_location: dropoff || pickup,
        pickup_datetime: toParisISO(formData.date, formData.time),

        // -------- RETURN LEG (ONLY for round trip) --------
        ...(formData.tripType === "return" && {
          return_pickup_location: formData.returnPickupLocation,
          return_dropoff_location: formData.returnDropoffLocation,
          return_pickup_datetime:
            formData.returnDate && formData.returnTime
              ? toParisISO(formData.returnDate, formData.returnTime)
              : null,
          return_pickup_address: formData.returnPickupAddress || null,
          return_dropoff_address: formData.returnDropoffAddress || null,
        }),

        adults: parseInt(formData.adults),
        kids: parseInt(formData.kids),
        luggage: parseInt(formData.luggage),
        hand_luggages: parseInt(formData.hand_luggages),
        checked_luggages: parseInt(formData.checked_luggages),
        hours: formData.tripType === "hourly" ? parseInt(formData.hours) : null,
        flight_detail_type: formData.flightDetailType,
        vehicle_type: vehicleInfo?.type || formData.vehicleChoice,
        arrival_flight_no: formData.arrivalFlightNo || null,
        arrival_from: formData.arrivalFrom || null,
        departure_flight_no: formData.departureFlightNo || null,
        departure_to: formData.departureTo || null,
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone,
        whatsapp_optin: formData.whatsappOptin,
        notes: formData.notes || null,
        night_surcharge: nightSurcharge || 0,
        payment_method: formData.paymentMethod,
        gdpr_consent: formData.gdprConsent,
        // Flag as quote-required if Other Location selected
        requires_quote: isQuoteRequired,
      };

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) throw new Error("Booking failed");

      const booking = await response.json();

      // Fire GA4 event (only if not a quote request)
      if (window.dataLayer && !isQuoteRequired) {
        window.dataLayer.push({
          event: "purchase",
          booking_id: booking.booking_id,
          value: booking.total_price,
          currency: "EUR",
        });
      }

      toast.success(
        isQuoteRequired ? "Quote request submitted!" : "Booking confirmed!",
      );
      navigate(
        `/thank-you?bookingId=${booking.booking_id}${isQuoteRequired ? "&quote=true" : ""}`,
      );
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(
        isQuoteRequired
          ? "Quote request failed. Please try again."
          : "Booking failed. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step validation
  const canProceed = (step) => {
    switch (step) {
      case 1:
        const hasPickup =
          formData.pickupLocation &&
          (!needsAddressInput(formData.pickupLocation) ||
            formData.pickupAddress);
        const hasDropoff =
          formData.tripType === "hourly" ||
          (formData.dropoffLocation &&
            (!needsAddressInput(formData.dropoffLocation) ||
              formData.dropoffAddress));
        return hasPickup && hasDropoff && formData.tripType;
      case 2:
        if (formData.tripType === "oneway") {
          const direction = formData.flightDetailType;
          if (pickupIsAirport && direction === "ARRIVAL") {
            return formData.date && formData.time && formData.arrivalFlightNo;
          } else {
            return formData.date && formData.time;
          }
          // else {
          //   return formData.date && formData.time && formData.departureFlightNo && formData.departureTo;
          // }
        }
        return formData.date && formData.time;
      case 3:
        return formData.adults && parseInt(formData.adults) >= 1;
      case 4:
        return (
          !!formData.customerName &&
          !!formData.customerEmail &&
          (formData?.customerPhone?.length || 0) > 8
        );
      case 5:
        return formData.gdprConsent && formData.termsConsent;
      default:
        return true;
    }
  };

  const locations = getLocations();
  const vehicleMode = getVehicleSelectionMode();
  const luggageInfo = getLuggageInfo();
  const vehiclesNeeded =
    vehicleMode === "multi-vehicle"
      ? calculateVehiclesNeeded(
          totalPassengers,
          luggageInfo.hand,
          luggageInfo.checked,
        )
      : null;

  const steps = [
    { id: 1, title: "Trip Type" },
    { id: 2, title: "Date & Time" },
    { id: 3, title: "Passengers" },
    { id: 4, title: "Contact" },
    { id: 5, title: "Confirm" },
  ];

  const AIRPORT_VALUES = new Set(
    TRANSFER_LOCATIONS.airports.locations.map((l) => l.value),
  );

  const pickupIsAirport = AIRPORT_VALUES.has(formData.pickupLocation);

  return (
    <div className="min-h-screen bg-paris-stone" data-testid="booking-page">
      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      currentStep >= step.id
                        ? "bg-champagne-gold text-midnight-navy"
                        : "bg-slate-200 text-slate-400"
                    }`}
                  >
                    {currentStep > step.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      step.id
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-8 md:w-16 h-1 mx-1 transition-all ${
                        currentStep > step.id
                          ? "bg-champagne-gold"
                          : "bg-slate-200"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-slate-600 mt-2">
              Step {currentStep}: {steps[currentStep - 1].title}
            </p>
          </div>

          {/* Form Container */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
            <AnimatePresence mode="wait">
              {/* Step 1: Trip Type */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-heading font-bold text-midnight-navy mb-4">
                    Select Trip Type
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        value: "oneway",
                        label: "One-Way",
                        desc: "Single trip",
                      },
                      {
                        value: "return",
                        label: "Round Trip",
                        desc: "Return included",
                      },
                      { value: "hourly", label: "Hourly", desc: "By the hour" },
                    ].map((type) => (
                      <button
                        key={type.value}
                        onClick={() => updateForm("tripType", type.value)}
                        className={`p-2 rounded-xl border-2 transition-all ${
                          formData.tripType === type.value
                            ? "border-champagne-gold bg-champagne-gold/10"
                            : "border-slate-200 hover:border-champagne-gold/50"
                        }`}
                        data-testid={`trip-type-${type.value}`}
                      >
                        <h3 className="font-bold text-midnight-navy text-lg">
                          {type.label}
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">
                          {type.desc}
                        </p>
                      </button>
                    ))}
                  </div>

                  <h2 className="text-2xl font-heading font-bold text-midnight-navy mb-4 mt-4">
                    {formData.tripType === "hourly"
                      ? "Starting Location"
                      : "Pickup & Drop-off Locations"}
                  </h2>
                  <div className="space-y-5">
                    {/* Pickup */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700 block">
                        <MapPin className="w-4 h-4 inline mr-1 text-eiffel-blue" />
                        Pickup Location *
                      </Label>
                      <Select
                        value={formData.pickupLocation}
                        onValueChange={(v) => updateForm("pickupLocation", v)}
                      >
                        <SelectTrigger data-testid="pickup-location-select">
                          <SelectValue placeholder="Select pickup location" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(locations).map(([key, category]) => (
                            <SelectGroup key={key}>
                              <SelectLabel className="text-xs text-slate-500 uppercase tracking-wider">
                                {category.label}
                              </SelectLabel>
                              {category.locations.map((loc) => (
                                <SelectItem key={loc.value} value={loc.value}>
                                  {loc.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          ))}
                        </SelectContent>
                      </Select>
                      {needsAddressInput(formData.pickupLocation) && (
                        <div className="mt-3 space-y-1.5">
                          <Label className="text-xs text-slate-600 block">
                            Enter exact address *
                          </Label>
                          <PlacesAutocomplete
                            placeholder="e.g., 15 Rue de la Marne, 77700 Serris"
                            value={formData.pickupAddress}
                            onChange={(value) =>
                              updateForm("pickupAddress", value)
                            }
                            className={
                              isOtherLocationPickup && !formData.pickupAddress
                                ? "border-red-300"
                                : ""
                            }
                            data-testid="pickup-address-input"
                          />
                          {isOtherLocationPickup && !formData.pickupAddress && (
                            <p className="text-xs text-red-500 flex items-center gap-1 mt-1.5">
                              <AlertCircle className="w-3 h-3" />
                              Please enter your pick-up location
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Dropoff (not for hourly) */}
                    {formData.tripType !== "hourly" && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 block">
                          <MapPin className="w-4 h-4 inline mr-1 text-champagne-gold" />
                          Drop-off Location *
                        </Label>
                        <Select
                          value={formData.dropoffLocation}
                          onValueChange={(v) =>
                            updateForm("dropoffLocation", v)
                          }
                        >
                          <SelectTrigger data-testid="dropoff-location-select">
                            <SelectValue placeholder="Select drop-off location" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(locations).map(
                              ([key, category]) => (
                                <SelectGroup key={key}>
                                  <SelectLabel className="text-xs text-slate-500 uppercase tracking-wider">
                                    {category.label}
                                  </SelectLabel>
                                  {category.locations.map((loc) => (
                                    <SelectItem
                                      key={loc.value}
                                      value={loc.value}
                                    >
                                      {loc.label}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                        {needsAddressInput(formData.dropoffLocation) && (
                          <div className="mt-3 space-y-1.5">
                            <Label className="text-xs text-slate-600 block">
                              Enter exact address *
                            </Label>
                            <PlacesAutocomplete
                              placeholder="e.g., 15 Rue de la Marne, 77700 Serris"
                              value={formData.dropoffAddress}
                              onChange={(value) =>
                                updateForm("dropoffAddress", value)
                              }
                              className={
                                isOtherLocationDropoff &&
                                !formData.dropoffAddress
                                  ? "border-red-300"
                                  : ""
                              }
                              data-testid="dropoff-address-input"
                            />
                            {isOtherLocationDropoff &&
                              !formData.dropoffAddress && (
                                <p className="text-xs text-red-500 flex items-center gap-1 mt-1.5">
                                  <AlertCircle className="w-3 h-3" />
                                  Please enter your drop-off location
                                </p>
                              )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quote Required Notice */}
                    {isQuoteRequired && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mt-2">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-amber-800 text-sm">
                              Custom Location - Quote Required
                            </p>
                            <p className="text-xs text-amber-700 mt-1">
                              Since you've selected "Other Location", we cannot
                              provide instant pricing. Complete the form and
                              we'll contact you with a personalized quote.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Return Trip Locations */}
                    {formData.tripType === "return" && (
                      <div className="mt-8">
                        <h3 className="text-xl font-heading font-bold text-midnight-navy mb-4">
                          Return Trip Locations
                        </h3>
                        <div className="space-y-5">
                          {/* Return Pickup */}
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-slate-700 block">
                              <MapPin className="w-4 h-4 inline mr-1 text-eiffel-blue" />
                              Return Pickup Location *
                            </Label>
                            <Select
                              value={formData.returnPickupLocation}
                              onValueChange={(v) =>
                                updateForm("returnPickupLocation", v)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select return pickup location" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(locations).map(
                                  ([key, category]) => (
                                    <SelectGroup key={key}>
                                      <SelectLabel className="text-xs text-slate-500 uppercase tracking-wider">
                                        {category.label}
                                      </SelectLabel>
                                      {category.locations.map((loc) => (
                                        <SelectItem
                                          key={loc.value}
                                          value={loc.value}
                                        >
                                          {loc.label}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                            {needsAddressInput(
                              formData.returnPickupLocation,
                            ) && (
                              <div className="mt-3 space-y-1.5">
                                <Label className="text-xs text-slate-600 block">
                                  Enter exact address *
                                </Label>
                                <PlacesAutocomplete
                                  placeholder="e.g., 15 Rue de la Marne, 77700 Serris"
                                  value={formData.returnPickupAddress}
                                  onChange={(value) =>
                                    updateForm("returnPickupAddress", value)
                                  }
                                />
                              </div>
                            )}
                          </div>

                          {/* Return Dropoff */}
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-slate-700 block">
                              <MapPin className="w-4 h-4 inline mr-1 text-champagne-gold" />
                              Return Drop-off Location *
                            </Label>
                            <Select
                              value={formData.returnDropoffLocation}
                              onValueChange={(v) =>
                                updateForm("returnDropoffLocation", v)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select return drop-off location" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(locations).map(
                                  ([key, category]) => (
                                    <SelectGroup key={key}>
                                      <SelectLabel className="text-xs text-slate-500 uppercase tracking-wider">
                                        {category.label}
                                      </SelectLabel>
                                      {category.locations.map((loc) => (
                                        <SelectItem
                                          key={loc.value}
                                          value={loc.value}
                                        >
                                          {loc.label}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                            {needsAddressInput(
                              formData.returnDropoffLocation,
                            ) && (
                              <div className="mt-3 space-y-1.5">
                                <Label className="text-xs text-slate-600 block">
                                  Enter exact address *
                                </Label>
                                <PlacesAutocomplete
                                  placeholder="e.g., 15 Rue de la Marne, 77700 Serris"
                                  value={formData.returnDropoffAddress}
                                  onChange={(value) =>
                                    updateForm("returnDropoffAddress", value)
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Hours for hourly booking */}
                    {formData.tripType === "hourly" && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 block">
                          <Clock className="w-4 h-4 inline mr-1 text-champagne-gold" />
                          Duration (hours) *
                        </Label>
                        <Select
                          value={formData.hours}
                          onValueChange={(v) => updateForm("hours", v)}
                        >
                          <SelectTrigger data-testid="hours-select">
                            <SelectValue placeholder="Select hours" />
                          </SelectTrigger>
                          <SelectContent>
                            {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                              <SelectItem key={h} value={String(h)}>
                                {h} hours
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500 mt-1">
                          Maximum 30km per hour included
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Step 2: Date & Time */}
              {currentStep === 2 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-heading font-bold text-midnight-navy mb-6">
                    Date & Time
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">
                        <Calendar className="w-4 h-4 inline mr-1 text-eiffel-blue" />
                        Pickup Date *
                      </Label>
                      <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal h-12"
                            data-testid="date-picker-trigger"
                          >
                            {formData.date
                              ? format(formData.date, "PPP")
                              : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={formData.date}
                            onSelect={(d) => {
                              updateForm("date", d);
                              setIsDateOpen(false);
                            }}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">
                        <Clock className="w-4 h-4 inline mr-1 text-eiffel-blue" />
                        Pickup Time *
                      </Label>
                      <Select
                        value={formData.time}
                        onValueChange={(v) => updateForm("time", v)}
                      >
                        <SelectTrigger
                          data-testid="time-select"
                          className="h-12"
                        >
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {TIME_SLOTS.map((slot) => (
                            <SelectItem key={slot} value={slot}>
                              {slot}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {formData.tripType === "return" && (
                    <>
                      <h3 className="text-2xl font-heading font-bold text-midnight-navy mb-6">
                        Return
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <Label className="text-sm font-medium text-slate-700 mb-2 block">
                            <Calendar className="w-4 h-4 inline mr-1 text-eiffel-blue" />
                            Pickup Date *
                          </Label>
                          <Popover
                            open={isReturnDateOpen}
                            onOpenChange={setIsReturnDateOpen}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal h-12"
                                data-testid="date-picker-trigger"
                              >
                                {formData.returnDate
                                  ? format(formData.returnDate, "PPP")
                                  : "Select date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <CalendarComponent
                                mode="single"
                                selected={formData.returnDate}
                                onSelect={(d) => {
                                  updateForm("returnDate", d);
                                  setIsReturnDateOpen(false);
                                }}
                                disabled={(date) => date < new Date()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <div>
                          <Label className="text-sm font-medium text-slate-700 mb-2 block">
                            <Clock className="w-4 h-4 inline mr-1 text-eiffel-blue" />
                            Pickup Time *
                          </Label>
                          <Select
                            value={formData.returnTime}
                            onValueChange={(v) => updateForm("returnTime", v)}
                          >
                            <SelectTrigger
                              data-testid="time-select"
                              className="h-12"
                            >
                              <SelectValue placeholder="Select time" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {TIME_SLOTS.map((slot) => (
                                <SelectItem key={slot} value={slot}>
                                  {slot}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </>
                  )}

                  <FlightDetailsSection
                    formData={formData}
                    updateForm={updateForm}
                    TRANSFER_LOCATIONS={TRANSFER_LOCATIONS}
                  />
                </motion.div>
              )}

              {/* Step 3: Passengers & Vehicle */}
              {currentStep === 3 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-heading font-bold text-midnight-navy mb-6">
                    Passengers & Vehicle
                  </h2>

                  {/* Passengers */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">
                        <Users className="w-4 h-4 inline mr-1 text-eiffel-blue" />
                        Adults *
                      </Label>
                      <Select
                        value={formData.adults}
                        onValueChange={(v) => updateForm("adults", v)}
                      >
                        <SelectTrigger
                          data-testid="adults-select"
                          className="h-12"
                        >
                          <SelectValue placeholder="Adults" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 30 }, (_, i) => i + 1).map(
                            (num) => (
                              <SelectItem key={num} value={String(num)}>
                                {num} {num === 1 ? "Adult" : "Adults"}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">
                        <Baby className="w-4 h-4 inline mr-1 text-eiffel-blue" />
                        Kids (0-12)
                      </Label>
                      <Select
                        value={formData.kids}
                        onValueChange={(v) => updateForm("kids", v)}
                      >
                        <SelectTrigger
                          data-testid="kids-select"
                          className="h-12"
                        >
                          <SelectValue placeholder="Kids" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 21 }, (_, i) => i).map(
                            (num) => (
                              <SelectItem key={num} value={String(num)}>
                                {num} {num === 1 ? "Child" : "Children"}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Luggages (v2 variables) */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">
                        🧳 Hand Luggages *
                      </Label>
                      <Select
                        value={formData.hand_luggages}
                        onValueChange={(v) => updateForm("hand_luggages", v)}
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Hand luggage" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 50 }, (_, i) => i).map(
                            (num) => (
                              <SelectItem key={num} value={String(num)}>
                                {num} {num === 1}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-2 block">
                        🛄 Checked Luggages
                      </Label>
                      <Select
                        value={formData.checked_luggages}
                        onValueChange={(v) => updateForm("checked_luggages", v)}
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Checked luggage" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 50 }, (_, i) => i).map(
                            (num) => (
                              <SelectItem key={num} value={String(num)}>
                                {num} {num === 1}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Vehicle Selection */}
                  <div className="mb-8">
                    <Label className="text-sm font-medium text-slate-700 mb-4 block">
                      Vehicle Selection
                    </Label>

                    {vehicleMode === "selectable" && (
                      <RadioGroup
                        value={formData.vehicleChoice}
                        onValueChange={(v) => updateForm("vehicleChoice", v)}
                        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                      >
                        <Label
                          htmlFor="car"
                          className={`flex items-center gap-3 sm:gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            formData.vehicleChoice === "Car"
                              ? "border-champagne-gold bg-champagne-gold/10"
                              : "border-slate-200 hover:border-champagne-gold/50"
                          }`}
                        >
                          <RadioGroupItem
                            value="Car"
                            id="car"
                            className="flex-shrink-0"
                          />
                          <Car className="w-8 h-8 text-midnight-navy flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-semibold text-midnight-navy">
                              Car
                            </p>
                            <p className="text-xs text-slate-500">
                              Max 3 passengers
                            </p>
                            <p className="text-xs text-slate-500">
                              2 checked + 2 hand luggage
                            </p>
                          </div>
                        </Label>
                        <Label
                          htmlFor="van"
                          className={`flex items-center gap-3 sm:gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            formData.vehicleChoice === "Van"
                              ? "border-champagne-gold bg-champagne-gold/10"
                              : "border-slate-200 hover:border-champagne-gold/50"
                          }`}
                        >
                          <RadioGroupItem
                            value="Van"
                            id="van"
                            className="flex-shrink-0"
                          />
                          <Bus className="w-8 h-8 text-midnight-navy flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-semibold text-midnight-navy">
                              Van
                            </p>
                            <p className="text-xs text-slate-500">
                              Max 8 passengers
                            </p>
                            <p className="text-xs text-slate-500">
                              8 checked + 8 hand luggage
                            </p>
                          </div>
                        </Label>
                      </RadioGroup>
                    )}

                    {vehicleMode === "van-only" && (
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex items-center gap-3">
                          <Bus className="w-10 h-10 text-eiffel-blue" />
                          <div>
                            <p className="font-semibold text-midnight-navy">
                              Van Required
                            </p>
                            <p className="text-sm text-slate-600">
                              A van is required for groups of 4-8 passengers.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {vehicleMode === "multi-vehicle" && vehiclesNeeded && (
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-eiffel-blue flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-midnight-navy mb-2">
                              Multiple Vehicles Required ({totalPassengers}{" "}
                              passengers)
                            </p>
                            <div className="flex gap-4 text-sm">
                              {vehiclesNeeded.vans > 0 && (
                                <span className="flex items-center gap-1">
                                  <Bus className="w-4 h-4" />
                                  {vehiclesNeeded.vans} Van
                                  {vehiclesNeeded.vans > 1 ? "s" : ""} (8 seats
                                  each)
                                </span>
                              )}
                              {vehiclesNeeded.cars > 0 && (
                                <span className="flex items-center gap-1">
                                  <Car className="w-4 h-4" />
                                  {vehiclesNeeded.cars} Car
                                  {vehiclesNeeded.cars > 1 ? "s" : ""} (3 seats
                                  each)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Luggage Info */}
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="font-medium text-midnight-navy mb-2 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Luggage Allowance
                    </h4>
                    <p className="text-sm text-slate-600">
                      {luggageInfo.checked} checked luggage + {luggageInfo.hand}{" "}
                      hand luggage included
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Contact Details */}
              {currentStep === 4 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-heading font-bold text-midnight-navy mb-6">
                    Contact Details
                  </h2>
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700 block">
                        <User className="w-4 h-4 inline mr-1" />
                        Full Name *
                      </Label>
                      <Input
                        value={formData.customerName}
                        onChange={(e) =>
                          updateForm("customerName", e.target.value)
                        }
                        placeholder="John Smith"
                        className="h-12"
                        data-testid="customer-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700 block">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email *
                      </Label>
                      <Input
                        type="email"
                        value={formData.customerEmail}
                        onChange={(e) =>
                          updateForm("customerEmail", e.target.value)
                        }
                        placeholder="john@example.com"
                        className="h-12"
                        data-testid="customer-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700 block">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone *
                      </Label>
                      <PhoneField
                        label="Phone"
                        required
                        defaultCountry="fr"
                        value={formData.customerPhone}
                        onChange={(val) => updateForm("customerPhone", val)}
                        testId="customer-phone"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Checkbox
                        id="whatsapp"
                        checked={formData.whatsappOptin}
                        onCheckedChange={(checked) =>
                          updateForm("whatsappOptin", checked)
                        }
                      />
                      <Label
                        htmlFor="whatsapp"
                        className="text-sm text-slate-600"
                      >
                        Send booking updates via WhatsApp
                      </Label>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700 block">
                        <MessageSquare className="w-4 h-4 inline mr-1" />
                        Special Requests
                      </Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => updateForm("notes", e.target.value)}
                        placeholder="Child seats, wheelchair access, etc."
                        rows={3}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 5: Confirm */}
              {currentStep === 5 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <h2 className="text-2xl font-heading font-bold text-midnight-navy mb-6">
                    Confirm Booking
                  </h2>

                  {/* Summary */}
                  <div className="bg-slate-50 rounded-xl p-4 mb-6">
                    <h3 className="font-semibold text-midnight-navy mb-4">
                      Booking Summary
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Trip Type:</span>
                        <span className="font-medium capitalize">
                          {formData.tripType}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">From:</span>
                        <span className="font-medium">
                          {formData.pickupLocation}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">To:</span>
                        <span className="font-medium">
                          {formData.dropoffLocation}
                        </span>
                      </div>
                      {formData.tripType === "return" && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Return From:</span>
                            <span className="font-medium">
                              {formData.returnPickupLocation}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Return To:</span>
                            <span className="font-medium">
                              {formData.returnDropoffLocation}
                            </span>
                          </div>
                        </>
                      )}
                      {formData.tripType === "hourly" && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Duration:</span>
                          <span className="font-medium">
                            {formData.hours} hours
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-600">Date & Time:</span>
                        <span className="font-medium">
                          {formData.date && format(formData.date, "PPP")} at{" "}
                          {formData.time}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Passengers:</span>
                        <span className="font-medium">
                          {`${formData.adults} ${formData.adults === 1 ? "Adult" : "Adults"}, ${formData.kids} ${formData.kids === 1 ? "Kid" : "Kids"}`}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Luggages:</span>
                        <span className="font-medium">
                          {formData.hand_luggages} Hand,{" "}
                          {formData.checked_luggages} Checked
                        </span>
                      </div>
                      {!isQuoteRequired && (
                        <div className="flex justify-between">
                          <span className="text-slate-600">Vehicle:</span>
                          <span className="font-medium flex items-center gap-2">
                            {vehicleInfo?.vehicles?.cars > 0 &&
                              `${vehicleInfo.vehicles.cars} Car${vehicleInfo.vehicles.cars > 1 ? "s" : ""}`}
                            {vehicleInfo?.vehicles?.cars > 0 &&
                              vehicleInfo?.vehicles?.vans > 0 &&
                              " + "}
                            {vehicleInfo?.vehicles?.vans > 0 &&
                              `${vehicleInfo.vehicles.vans} Van${vehicleInfo.vehicles.vans > 1 ? "s" : ""}`}
                          </span>
                        </div>
                      )}
                    </div>
                    {/* Price Section with Separator */}
                    {isQuoteRequired || calculatedPrice !== null ? (
                      <div className="border-t border-slate-200 mt-4 pt-4 flex justify-between items-center">
                        <span className="text-lg font-semibold text-midnight-navy">
                          {isQuoteRequired ? "Status:" : "Total:"}
                        </span>
                        {isQuoteRequired ? (
                          <span className="text-lg font-bold text-amber-600">
                            Quote Required
                          </span>
                        ) : (
                          <span className="text-2xl font-bold text-champagne-gold">
                            €{calculatedPrice}
                          </span>
                        )}
                      </div>
                    ) : (
                      /* No price yet - show placeholder with proper spacing */
                      <div className="border-t border-slate-200 mt-4 pt-4 flex justify-between items-center">
                        <span className="text-lg font-semibold text-midnight-navy">
                          Total:
                        </span>
                        <span className="text-lg font-medium text-slate-400">
                          Calculating...
                        </span>
                      </div>
                    )}
                    {/* Night Pricing Notice in Summary */}
                    {showNightPricingNotice && (
                      <div className="mt-3 p-3 bg-indigo-50 rounded-lg flex items-center gap-2">
                        <Clock className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        <span className="text-xs text-indigo-700 font-medium">
                          Night pricing (+€15) applied for pickups 22:00–06:00
                        </span>
                      </div>
                    )}
                    {isQuoteRequired && (
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                        <p className="text-xs text-amber-700">
                          We'll review your request and contact you with a
                          personalized price quote within 2 hours.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div className="mb-6">
                    <Label className="text-sm font-medium text-slate-700 mb-4 block">
                      <CreditCard className="w-4 h-4 inline mr-1" />
                      Payment Method
                    </Label>
                    <RadioGroup
                      value={formData.paymentMethod}
                      onValueChange={(v) => updateForm("paymentMethod", v)}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                    >
                      <Label
                        htmlFor="cash"
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer ${
                          formData.paymentMethod === "cash"
                            ? "border-champagne-gold bg-champagne-gold/10"
                            : "border-slate-200"
                        }`}
                      >
                        <RadioGroupItem
                          value="cash"
                          id="cash"
                          className="flex-shrink-0"
                        />
                        <span>Cash to Driver</span>
                      </Label>
                      <Label
                        htmlFor="card"
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer ${
                          formData.paymentMethod === "card"
                            ? "border-champagne-gold bg-champagne-gold/10"
                            : "border-slate-200"
                        }`}
                      >
                        <RadioGroupItem
                          value="card"
                          id="card"
                          className="flex-shrink-0"
                        />
                        <span>Card to Driver</span>
                      </Label>
                    </RadioGroup>
                  </div>

                  {/* Consents */}
                  <div className="space-y-3 mt-6">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="terms"
                        checked={formData.termsConsent}
                        onCheckedChange={(checked) =>
                          updateForm("termsConsent", checked)
                        }
                        className="mt-0.5"
                      />
                      <Label
                        htmlFor="terms"
                        className="text-sm text-slate-600 leading-relaxed"
                      >
                        I agree to the{" "}
                        <Link
                          to="/terms"
                          className="text-eiffel-blue hover:underline"
                          target="_blank"
                        >
                          Terms and Conditions
                        </Link>{" "}
                        *
                      </Label>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="gdpr"
                        checked={formData.gdprConsent}
                        onCheckedChange={(checked) =>
                          updateForm("gdprConsent", checked)
                        }
                        className="mt-0.5"
                      />
                      <Label
                        htmlFor="gdpr"
                        className="text-sm text-slate-600 leading-relaxed"
                      >
                        I consent to the processing of my personal data in
                        accordance with the{" "}
                        <Link
                          to="/privacy"
                          className="text-eiffel-blue hover:underline"
                          target="_blank"
                        >
                          Privacy Policy
                        </Link>{" "}
                        *
                      </Label>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Price Summary Bar */}
            {isQuoteRequired ? (
              <div className="mt-6 mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <div>
                      <span className="text-sm font-semibold text-amber-800">
                        Quote Required
                      </span>
                      <p className="text-xs text-amber-700">
                        Custom location pricing will be provided by our team
                      </p>
                    </div>
                  </div>
                  <span className="font-heading font-bold text-lg text-amber-800">
                    Contact for Price
                  </span>
                </div>
                {showNightPricingNotice && (
                  <div className="mt-3 pt-3 border-t border-amber-200 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm text-indigo-700 font-medium">
                      Night pricing (+€15) applied for pickups 22:00–06:00
                    </span>
                  </div>
                )}
              </div>
            ) : calculatedPrice !== null ? (
              <div className="mt-6 mb-6 p-4 bg-champagne-gold/10 border border-champagne-gold/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-600">
                      Estimated Price:
                    </span>
                    {vehicleInfo?.vehicles && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {vehicleInfo.vehicles.cars > 0 && (
                          <span className="flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            {vehicleInfo.vehicles.cars} Car
                            {vehicleInfo.vehicles.cars > 1 ? "s" : ""}
                          </span>
                        )}
                        {vehicleInfo.vehicles.vans > 0 && (
                          <span className="flex items-center gap-1">
                            <Bus className="w-3 h-3" />
                            {vehicleInfo.vehicles.vans} Van
                            {vehicleInfo.vehicles.vans > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span className="font-heading font-bold text-2xl text-midnight-navy">
                    €{calculatedPrice}
                  </span>
                </div>
                {showNightPricingNotice && (
                  <div className="mt-3 pt-3 border-t border-champagne-gold/30 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm text-indigo-700 font-medium">
                      Night pricing (+€15) applied for pickups 22:00–06:00
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6 mb-6 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Info className="w-5 h-5 text-slate-400" />
                    <span className="text-sm text-slate-500">
                      Complete the form to see your price
                    </span>
                  </div>
                  <span className="font-heading font-bold text-lg text-slate-300">
                    €--
                  </span>
                </div>
                {showNightPricingNotice && (
                  <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm text-indigo-700 font-medium">
                      Night pricing (+€15) applied for pickups 22:00–06:00
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-6 border-t">
              {currentStep > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep((s) => s - 1)}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}

              {currentStep < 5 ? (
                <Button
                  type="button"
                  onClick={() => setCurrentStep((s) => s + 1)}
                  disabled={!canProceed(currentStep)}
                  className="gap-2 bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !canProceed(6)}
                  className={`gap-2 ${
                    isQuoteRequired
                      ? "bg-amber-500 hover:bg-amber-600 text-white"
                      : "bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy"
                  }`}
                  data-testid="confirm-booking-btn"
                >
                  {isSubmitting
                    ? "Processing..."
                    : isQuoteRequired
                      ? "Request Quote"
                      : "Confirm Booking"}
                  <Shield className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Booking;
